import i18n from "../i18n";
import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  KeyRound,
  Mail,
  Flame,
  Activity,
  Loader2,
  Save,
  SendHorizonal,
  CheckCircle2,
  XCircle,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { api } from "../lib/api";
import { AdminClients } from "./AdminClients";
import { AdminWorkspaces } from "./AdminWorkspaces";
import { AdminUsers } from "./AdminUsers";
import { AdminContactMessages } from "./AdminContactMessages";
import { CommercialCatalog } from "./CommercialCatalog";

const STATUS_STYLES = {
  active: "bg-cyan-500/15 text-cyan-600 border-cyan-500/30",
  burned: "bg-red-500/15 text-red-600 border-red-500/30",
  expired: "bg-slate-500/15 text-slate-500 border-slate-500/30",
};

const STATUS_LABEL = { active: "Actif", burned: "Brûlé", expired: "Expiré" };
const EMAIL_LABEL = { sent: "Envoyé", mocked: "Simulé", failed: "Échec", none: "—" };

export const AdminStatsAndSettings = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ sender_email: "", logo_url: "", resend_api_key: "", dolibarr_url: "" });
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [view, setView] = useState("dashboard");

  const load = useCallback(async () => {
    try {
      const [s, cfg] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/settings"),
      ]);
      setStats(s.data);
      setSettings(cfg.data);
      setForm({
        sender_email: cfg.data.sender_email || "",
        logo_url: cfg.data.logo_url || "",
        resend_api_key: "",
        dolibarr_url: cfg.data.dolibarr_url || "",
      });
    } catch (e) {
      if (e.response?.status === 401) onLogout?.();
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings", {
        sender_email: form.sender_email,
        logo_url: form.logo_url,
        resend_api_key: form.resend_api_key || null,
        dolibarr_url: form.dolibarr_url,
      });
      toast.success("Paramètres enregistrés");
      await load();
      setForm((f) => ({ ...f, resend_api_key: "" }));
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) {
      toast.error("Saisissez une adresse e-mail de test.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/test-email", { recipient_email: testEmail });
      if (data.status === "sent") toast.success(`E-mail de test envoyé à ${testEmail}`);
      else if (data.status === "mocked")
        toast.info("Mode démo : e-mail simulé (aucune clé API configurée).");
      else toast.error("Échec de l'envoi de l'e-mail de test.");
    } catch (e) {
      toast.error("Échec de l'envoi.");
    } finally {
      setTesting(false);
    }
  };

  if (!stats || !settings) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const metrics = [
    { label: "Secrets créés", value: stats.total_secrets, icon: KeyRound, color: "text-emerald-600", testid: "metric-total-secrets" },
    { label: "E-mails envoyés", value: stats.emails_sent, icon: Mail, color: "text-cyan-600", testid: "metric-emails-sent" },
    { label: "Secrets brûlés", value: stats.secrets_burned, icon: Flame, color: "text-red-600", testid: "metric-secrets-burned" },
    { label: "Taux de consultation", value: `${stats.view_rate}%`, icon: Activity, color: "text-amber-600", testid: "metric-view-rate" },
  ];

  return (
    <div data-testid="admin-dashboard-container" className="space-y-8 overflow-x-hidden">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600">
            Console d'administration
          </span>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight mt-1">
            Statistiques & configuration
          </h2>
        </div>
        <Button
          data-testid="admin-logout-btn"
          onClick={onLogout}
          variant="outline"
          className="border-slate-200 bg-slate-100 hover:bg-slate-100 text-slate-900 shrink-0"
        >
          <LogOut className="w-4 h-4 mr-2" /> Déconnexion
        </Button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit max-w-full overflow-x-auto">
        {[
          { key: "dashboard", label: "Tableau de bord" },
          { key: "workspaces", label: "Workspaces" },
          { key: "clients", label: "Clients" },
          { key: "users", label: "Utilisateurs" },
          { key: "billing", label: "Facturation" },
          { key: "commercial", label: "Configuration commerciale" },
          { key: "contacts", label: "Contacts" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setView(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              view === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
            {t.key === "contacts" && stats.contact_unread > 0 && (
              <span
                data-testid="contact-unread-badge"
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-emerald-950 text-[10px] font-bold"
              >
                {stats.contact_unread}
              </span>
            )}
            {t.key === "workspaces" && stats.pending_join_requests > 0 && (
              <span
                data-testid="pending-join-requests-badge"
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-amber-950 text-[10px] font-bold"
              >
                {stats.pending_join_requests}
              </span>
            )}
          </button>
        ))}
      </div>

      {view === "clients" ? (
        <AdminClients section="clients" />
      ) : view === "workspaces" ? (
        <AdminWorkspaces />
      ) : view === "users" ? (
        <AdminUsers />
      ) : view === "billing" ? (
        <AdminClients section="billing" />
      ) : view === "commercial" ? (
        <CommercialCatalog />
      ) : view === "contacts" ? (
        <AdminContactMessages />
      ) : (
      <>
      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((m, i) => (
          <motion.div
            key={m.label}
            data-testid={m.testid}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            className="glass rounded-2xl p-5"
          >
            <m.icon className={`w-5 h-5 ${m.color} mb-3`} />
            <div className="font-heading text-3xl font-extrabold">{m.value}</div>
            <div className="text-xs text-slate-500 mt-1">{m.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings form */}
        <div data-testid="form-smtp-settings" className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg font-semibold mb-1">Service e-mail (Resend)</h3>
          <div className="flex items-center gap-2 mb-5 text-sm">
            {settings.api_key_configured ? (
              <span className="flex items-center gap-1.5 text-emerald-600">
                <CheckCircle2 className="w-4 h-4" /> Clé configurée ({settings.api_key_masked})
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-amber-600">
                <XCircle className="w-4 h-4" /> Aucune clé — mode démo (e-mails simulés)
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">Clé API Resend</Label>
              <Input
                data-testid="input-smtp-api-key"
                type="password"
                placeholder="re_..."
                value={form.resend_api_key}
                onChange={(e) => setForm((f) => ({ ...f, resend_api_key: e.target.value }))}
                className="bg-slate-50 border-slate-200 font-mono-token"
              />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Expéditeur personnalisé</Label>
              <Input
                data-testid="input-custom-sender-email"
                placeholder="noreply@votredomaine.com"
                value={form.sender_email}
                onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))}
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">URL du logo (e-mails)</Label>
              <Input
                data-testid="input-custom-logo-url"
                placeholder="https://.../logo.png"
                value={form.logo_url}
                onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
                className="bg-slate-50 border-slate-200"
              />
            </div>

            <div className="border-t border-slate-200 pt-4 mt-1">
              <div className="flex items-center gap-2 mb-3 text-sm">
                {settings.dolibarr_configured ? (
                  <span className="flex items-center gap-1.5 text-emerald-600">
                    <CheckCircle2 className="w-4 h-4" /> Dolibarr connecté
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-slate-500">
                    <XCircle className="w-4 h-4" /> Dolibarr non configuré (facturation en attente)
                  </span>
                )}
              </div>
              <Label className="text-slate-600 mb-1.5 block">URL Dolibarr</Label>
              <Input
                data-testid="input-dolibarr-url"
                placeholder="https://votre-dolibarr.nc"
                value={form.dolibarr_url}
                onChange={(e) => setForm((f) => ({ ...f, dolibarr_url: e.target.value }))}
                className="bg-slate-50 border-slate-200 mb-3"
              />
              <p data-testid="dolibarr-api-key-env-note" className="text-xs text-slate-500">
                {i18n.t("billing:v1.adminDolibarrKeyEnv")}
              </p>
            </div>

            <Button
              data-testid="btn-save-admin-settings"
              onClick={saveSettings}
              disabled={saving}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>

          <div className="border-t border-slate-200 mt-6 pt-5">
            <Label className="text-slate-600 mb-1.5 block">Tester l'envoi</Label>
            <div className="flex gap-2">
              <Input
                data-testid="input-test-email"
                placeholder="test@exemple.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="bg-slate-50 border-slate-200"
              />
              <Button
                data-testid="btn-send-test-email"
                onClick={sendTest}
                disabled={testing}
                variant="outline"
                className="border-slate-200 bg-slate-100 hover:bg-slate-100 shrink-0"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Recent activity */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Activité récente</h3>
          {stats.recent.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">
              Aucun secret créé pour le moment.
            </p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {stats.recent.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 truncate">
                      {r.recipient_email || "Lien direct"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(r.created_at).toLocaleString("fr-FR")} · {EMAIL_LABEL[r.email_status] || "—"}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`shrink-0 ${STATUS_STYLES[r.status] || ""}`}
                  >
                    {STATUS_LABEL[r.status] || r.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};
